/**
 * AsteroidEntity.ts
 * Pooled drifting obstacle for the World 2 space shmup.
 *
 * Two flavours, picked by `size`:
 *   - `sm` / `md` — destructible. Take damage from ship projectiles, despawn at 0 HP.
 *   - `lg` / `xl` — indestructible. Projectiles are absorbed by the collision
 *     system (no damage dealt, no ricochet); the asteroid keeps drifting.
 *
 * Movement is a constant leftward drift in world space at
 * `ASTEROID_DRIFT_SPEED`. The camera also auto-scrolls right at
 * `SPACE_AUTO_SCROLL_SPEED`, so on-screen the asteroid appears to travel
 * left at `SPACE_AUTO_SCROLL_SPEED + ASTEROID_DRIFT_SPEED`. Despawn rules:
 *   - HP reaches 0 (destructible only).
 *   - Right edge passes `ASTEROID_OFFSCREEN_MARGIN` left of the camera.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module AsteroidEntity
 */

import { BaseEntity } from './BaseEntity';
import type { LevelRuntime } from '../engine/gameState';
import { spaceState } from '../engine/spaceState';
import {
  ASTEROID_SIZE_PX,
  ASTEROID_HP,
  ASTEROID_DRIFT_SPEED,
  ASTEROID_OFFSCREEN_MARGIN,
  isDestructible,
  type AsteroidSize,
} from '../../constants/asteroidConstants';

/**
 * Pooled asteroid obstacle. Multiple instances live in the
 * `asteroidPool` (see `asteroidSystem.ts`); use `spawn()` after `acquire()`.
 */
export class AsteroidEntity extends BaseEntity {
  /** Monotonic ID counter for stable React keys across pool recycling. */
  private static nextSpawnId = 1;

  /** Re-issued on every spawn so the renderer gets a stable key per "use". */
  public spawnId = 0;

  /** Active size tier; rewritten on every `spawn()` (also drives `width`/`height`). */
  public size: AsteroidSize = 'sm';

  /** Remaining HP. `-1` means indestructible (lg/xl). */
  public hp = 0;

  /** True while this asteroid is in the active list. */
  public active = false;

  constructor() {
    // Pooled instances start in a consistent 'sm' state so prewarmed,
    // never-spawned entities never report a `size` that disagrees with
    // `width`/`height`. Both are overwritten on every `spawn()`.
    super(0, 0, ASTEROID_SIZE_PX.sm, ASTEROID_SIZE_PX.sm);
    this.dead = true;
  }

  /**
   * Initialise the asteroid at a world position with a given size tier.
   *
   * @param x    - Spawn X (world space, top-left of hitbox).
   * @param y    - Spawn Y (world space, top-left of hitbox).
   * @param size - Size tier; determines sprite, hitbox, HP, and destructibility.
   */
  public spawn(x: number, y: number, size: AsteroidSize): void {
    this.spawnId = AsteroidEntity.nextSpawnId++;
    this.size = size;
    const dim = ASTEROID_SIZE_PX[size];
    this.width = dim;
    this.height = dim;
    this.x = x;
    this.y = y;
    this.vx = -ASTEROID_DRIFT_SPEED;
    this.vy = 0;
    this.hp = ASTEROID_HP[size];
    this.dead = false;
    this.active = true;
  }

  /** Pool-recycle hook. */
  public reset(): void {
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.hp = 0;
    this.dead = true;
    this.active = false;
    this.size = 'sm';
    // Keep width/height in sync with `size` so the public hitbox state never
    // disagrees with `size` while the asteroid is parked in the pool.
    this.width = ASTEROID_SIZE_PX.sm;
    this.height = ASTEROID_SIZE_PX.sm;
  }

  /** True iff this asteroid can be killed by a projectile. */
  public get destructible(): boolean {
    return isDestructible(this.size);
  }

  /**
   * Apply `amount` damage. No-op on indestructible asteroids. Marks the
   * asteroid dead when HP reaches 0 so the spawner reaps it next tick.
   *
   * @param amount - Damage in HP units. Must be > 0; non-positive values are ignored.
   * @returns `true` if the asteroid was killed by this hit.
   */
  public takeDamage(amount: number): boolean {
    if (!this.destructible || this.dead) return false;
    // Guard against non-positive damage so a buggy caller can't HEAL an
    // asteroid by passing a negative `amount` (mirrors NovaEntity.applyHit).
    if (amount <= 0) return false;
    this.hp -= amount;
    if (this.hp <= 0) {
      this.hp = 0;
      this.dead = true;
      this.active = false;
      return true;
    }
    return false;
  }

  /**
   * Advance one fixed timestep. Integrates velocity and culls when the
   * asteroid drifts past the left edge of the camera window.
   *
   * @param dt    - Fixed timestep in seconds.
   * @param level - Unused (space has no level geometry).
   */
  public update(dt: number, level: LevelRuntime): void {
    void level;
    if (!this.active) return;

    this.x += this.vx * dt;
    this.y += this.vy * dt;

    // DECISION: Camera-relative left cull. Mirror of the ShipProjectile
    // right-edge rule — compare the asteroid's RIGHT edge (x + width) against
    // the camera's left edge minus the margin, so a wider asteroid does not
    // vanish while still partly on screen.
    const leftCullX = spaceState.cameraX - ASTEROID_OFFSCREEN_MARGIN;
    if (this.x + this.width < leftCullX) {
      this.dead = true;
      this.active = false;
    }
  }
}
